/**
 * /field/ew/tickets/:id — one field ticket on the crew's phone (EW-5,
 * GET /earthworks/field/tickets/:id). SNAKE_CASE.
 *
 * NO PRICES FOR A FIELD HAND. The API already strips rates, amounts and the
 * subtotal for a field_worker (withoutPrices); this screen ALSO draws no price
 * column unless the role may read rates (`canSeeEwTicketPrices`), so a payload
 * that ever carried them would still not put a dollar figure on a hand's
 * phone in front of the customer.
 *
 * Sign-off while DRAFT, two ways: the customer signs on the phone (name +
 * signature, EwSignForm), or the crew photographs the customer-signed paper
 * ticket (uploaded to the job's files, then POST …/paper with its upload id).
 */
import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Camera } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { uploadEwJobFile } from '../../../hooks/useEwField';
import { useEwFieldTicket, usePaperEwFieldTicket, useSignEwFieldTicket } from '../../../hooks/useEwTickets';
import { ewDay, ewInstant, ewRefusal, fmtMoney, fmtNumber } from '../../../lib/earthworks';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { canSeeEwTicketPrices, ewSignedVia, ewTicketEditable, ewTicketSkin } from '../../../lib/ew-tickets';
import {
  PT_FIELD_SECONDARY,
  PtFieldBadge,
  PtFieldCard,
  PtFieldPage,
  PtFieldQueryState,
  PtFieldSectionTitle,
  PtRefusal,
} from '../../pressure-testing/field/PtFieldBits';
import { ewNotOnCrewMessage } from './EwFieldJobScreen';
import { EwSignForm } from './EwSignForm';

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-b border-fieldiq-border px-4 py-2.5 last:border-b-0">
      <span className="text-sm text-fieldiq-muted">{label}</span>
      <span className="text-right text-sm font-semibold text-white">{value}</span>
    </div>
  );
}

export default function EwFieldTicketScreen() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const prices = canSeeEwTicketPrices(user?.role);
  const tz = useTenantTimeZone();
  const query = useEwFieldTicket(id);
  const sign = useSignEwFieldTicket();
  const paper = usePaperEwFieldTicket();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = query.data ?? null;
  const refused = ewNotOnCrewMessage(query);
  const draft = !!t && ewTicketEditable(t.status);
  const lines = t?.lines ?? [];

  const onPaper = async (file: File | undefined) => {
    if (!file || !t) return;
    setError(null);
    setUploading(true);
    try {
      const uploadId = await uploadEwJobFile(t.job_id, file);
      await paper.mutateAsync({ id: t.id, body: { upload_id: uploadId } });
      toast.success(`${t.ticket_number} signed on paper`);
    } catch (err) {
      setError(ewRefusal(err, 'The paper ticket could not be attached.'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <PtFieldPage title={t?.ticket_number || 'Ticket'} backTo={t ? `/field/ew/jobs/${t.job_id}` : '/field/ew'} testId="ew-field-ticket">
      {refused ? (
        <PtRefusal message={refused} testId="ew-field-not-on-crew" />
      ) : (
        <PtFieldQueryState query={query} subject="this ticket">
          {!t ? (
            <PtRefusal message="That ticket no longer exists. It may have been voided or deleted." />
          ) : (
            <div className="space-y-4">
              <PtFieldCard>
                <div className="flex items-center justify-between gap-2 border-b border-fieldiq-border px-4 py-3">
                  <span className="text-base font-bold text-white">{t.customer_name || '—'}</span>
                  <PtFieldBadge skin={ewTicketSkin(t.status)} />
                </div>
                <Row label="Job" value={t.job_number || '—'} />
                <Row label="Site" value={t.site_name || '—'} />
                <Row label="Work day" value={t.mode === 'per_day' ? ewDay(t.work_date) : t.mode_label || 'Whole job'} />
                {prices && <Row label={t.status === 'draft' ? 'Subtotal so far' : 'Subtotal'} value={fmtMoney(t.status === 'draft' ? t.draft_subtotal : t.subtotal, t.currency)} />}
                {t.void_reason && <p className="px-4 py-3 text-sm text-fieldiq-text">Void: {t.void_reason}</p>}
              </PtFieldCard>

              <section>
                <PtFieldSectionTitle>Lines · {lines.length}</PtFieldSectionTitle>
                <PtFieldCard>
                  {lines.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-fieldiq-muted">No lines on this ticket.</p>
                  ) : (
                    lines.map((l) => (
                      <div key={l.id} data-testid="ew-field-ticket-line" className="border-b border-fieldiq-border px-4 py-2.5 last:border-b-0">
                        <div className="flex justify-between gap-3">
                          <span className="min-w-0 text-sm font-semibold text-white">{l.description || l.kind_label || '—'}</span>
                          <span className="shrink-0 text-right text-sm text-white">
                            {fmtNumber(l.quantity)} {l.unit_label || l.unit}
                          </span>
                        </div>
                        <div className="mt-0.5 flex justify-between gap-3 text-xs text-fieldiq-muted">
                          <span>{l.kind_label || l.kind}{l.work_date ? ` · ${ewDay(l.work_date)}` : ''}{l.standby ? ' · standby' : ''}</span>
                          {prices && <span className="font-mono" data-testid="ew-field-ticket-line-amount">{fmtMoney(l.amount ?? null, t.currency)}</span>}
                        </div>
                      </div>
                    ))
                  )}
                </PtFieldCard>
              </section>

              {draft ? (
                <>
                  {error && <PtRefusal message={error} testId="ew-field-ticket-error" />}
                  <EwSignForm
                    heading="Customer sign-off"
                    intro="Hand the phone to the customer's representative to review the lines and sign."
                    submitLabel="Sign ticket"
                    pending={sign.isPending}
                    error={null}
                    testId="ew-field-ticket-sign"
                    onSubmit={(body) => {
                      setError(null);
                      sign.mutate({ id: t.id, body }, {
                        onSuccess: () => toast.success(`${t.ticket_number} signed`),
                        onError: (err) => setError(ewRefusal(err, 'The ticket could not be signed.')),
                      });
                    }}
                  />
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,application/pdf"
                    capture="environment"
                    className="hidden"
                    aria-label="Photo of the signed paper ticket"
                    onChange={(e) => onPaper(e.target.files?.[0])}
                  />
                  <button type="button" className={PT_FIELD_SECONDARY} disabled={uploading || paper.isPending} onClick={() => fileRef.current?.click()} data-testid="ew-field-ticket-paper">
                    <Camera className="size-5" aria-hidden="true" />
                    {uploading ? 'Uploading…' : 'Signed on paper? Photograph it'}
                  </button>
                </>
              ) : (
                <PtFieldCard>
                  <Row label="Signed" value={t.signed_via ? `${ewSignedVia(t.signed_via)} · ${ewInstant(t.signed_at, tz)}` : '—'} />
                  <Row label="Signer" value={t.signer_name ? `${t.signer_name}${t.signer_title ? `, ${t.signer_title}` : ''}` : '—'} />
                  {t.signature_url && <div className="px-4 py-3"><img src={t.signature_url} alt="Signature" className="h-20 rounded bg-white p-1" /></div>}
                </PtFieldCard>
              )}
            </div>
          )}
        </PtFieldQueryState>
      )}
    </PtFieldPage>
  );
}
